import { useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChecklistIcon from '@mui/icons-material/Checklist';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { CodeChip } from '../atoms';
import { useCatalogCart } from '../../hooks/useCatalogCart';
import type { CartItem } from '../../cartSlice';

import { ImportDialog } from './ImportDialog';

import { useAppSelector } from '@/shared/hooks/redux';
import { useAuth } from '@/shared/components/AuthProvider';

const RAIL_WIDTH = 320;
const RAIL_COLLAPSED_WIDTH = 52;

function useGroupedItems(items: Record<string, CartItem>) {
  return useMemo(() => {
    const groups = new Map<string, CartItem[]>();
    for (const item of Object.values(items)) {
      const key = item.node.jurisdiction_title;
      const list = groups.get(key) ?? [];
      list.push(item);
      groups.set(key, list);
    }
    return [...groups.entries()];
  }, [items]);
}

function TrayList({
  grouped,
  onRemove,
}: {
  grouped: [string, CartItem[]][];
  onRemove: (id: string) => void;
}) {
  return (
    <Box sx={{ flex: 1, overflow: 'auto' }}>
      {grouped.map(([source, sourceItems]) => (
        <Box key={source}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{
              display: 'block',
              px: 2,
              pt: 1.5,
              position: 'sticky',
              top: 0,
              bgcolor: 'background.paper',
            }}
          >
            {source} · {sourceItems.length}
          </Typography>
          {sourceItems.map((item) => (
            <Box
              key={item.node.id}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.75 }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <CodeChip code={item.node.display_code} />
                  <Typography variant="body2" noWrap>
                    {item.node.description || item.node.display_code}
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {!item.withDescendants
                    ? 'Single standard'
                    : item.excludedIds.length > 0
                      ? 'Part of section'
                      : 'Whole section'}
                </Typography>
              </Box>
              <Tooltip title="Remove">
                <IconButton size="small" onClick={() => onRemove(item.node.id)} aria-label="Remove">
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
          <Divider sx={{ mt: 1 }} />
        </Box>
      ))}
    </Box>
  );
}

export function SelectionTray() {
  const { user } = useAuth();
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));

  const items = useAppSelector((state) => state.standardsCart.items);
  const { removeNode, clear } = useCatalogCart();
  const grouped = useGroupedItems(items);

  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const count = Object.keys(items).length;
  if (count === 0) return null;

  const importDialog = user?.id ? (
    <ImportDialog
      open={importOpen}
      ownerId={user.id}
      onClose={() => {
        setImportOpen(false);
        setMobileOpen(false);
      }}
    />
  ) : null;

  // ---- Mobile: fixed bottom bar -> full-screen Selected drawer ----
  if (isNarrow) {
    return (
      <>
        <Box
          sx={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: (t) => t.zIndex.appBar,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1,
            bgcolor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
            boxShadow: '0 -1px 4px rgba(0,0,0,0.08)',
          }}
        >
          <Badge badgeContent={count} color="primary">
            <ChecklistIcon />
          </Badge>
          <Typography variant="subtitle2" sx={{ flex: 1 }}>
            {count} selected
          </Typography>
          <Button size="small" onClick={() => setMobileOpen(true)}>
            Review
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={!user?.id}
            onClick={() => setImportOpen(true)}
          >
            Import
          </Button>
        </Box>

        <Drawer
          anchor="bottom"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          PaperProps={{ sx: { height: '90vh', display: 'flex', flexDirection: 'column' } }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 2,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="h6" sx={{ flex: 1 }}>
              Selected ({count})
            </Typography>
            <Button size="small" color="inherit" onClick={clear}>
              Clear all
            </Button>
            <IconButton size="small" onClick={() => setMobileOpen(false)} aria-label="Close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <TrayList grouped={grouped} onRemove={removeNode} />
          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 1 }}>
            <Button fullWidth color="inherit" onClick={() => setMobileOpen(false)}>
              Keep selecting
            </Button>
            <Button
              fullWidth
              variant="contained"
              disabled={!user?.id}
              onClick={() => setImportOpen(true)}
            >
              Review &amp; import
            </Button>
          </Box>
        </Drawer>
        {importDialog}
      </>
    );
  }

  // ---- Desktop: collapsed thin rail ----
  if (collapsed) {
    return (
      <Box
        sx={{
          width: RAIL_COLLAPSED_WIDTH,
          flex: 'none',
          alignSelf: 'stretch',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 1,
          gap: 1,
          borderLeft: 1,
          borderColor: 'divider',
        }}
      >
        <Tooltip title="Show selected" placement="left">
          <IconButton onClick={() => setCollapsed(false)} aria-label="Show selected">
            <Badge badgeContent={count} color="primary">
              <ChecklistIcon />
            </Badge>
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  // ---- Desktop: full rail ----
  return (
    <>
      <Box
        sx={{
          width: RAIL_WIDTH,
          flex: 'none',
          alignSelf: 'stretch',
          display: 'flex',
          flexDirection: 'column',
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          maxHeight: 'calc(100vh - 200px)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <ChecklistIcon fontSize="small" color="action" />
          <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 600 }}>
            Selected ({count})
          </Typography>
          <Button size="small" color="inherit" onClick={clear}>
            Clear all
          </Button>
          <Tooltip title="Collapse">
            <IconButton
              size="small"
              onClick={() => setCollapsed(true)}
              aria-label="Collapse selected"
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <TrayList grouped={grouped} onRemove={removeNode} />

        <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Button
            fullWidth
            variant="contained"
            disabled={!user?.id}
            onClick={() => setImportOpen(true)}
          >
            Review &amp; import
          </Button>
        </Box>
      </Box>
      {importDialog}
    </>
  );
}

export default SelectionTray;
